import moment from 'moment';

export const DEFAULT_EVENTS_VIEW_SETTINGS = {
  searchText: '',
  status: 'active',
  dateWindow: 'all',
  publicState: 'all',
  registrationState: 'all',
  galleryState: 'all',
  capacityState: 'all',
  sortOrder: 'startDate|desc',
};

const STORAGE_PREFIX = 'manageEventsView';

const VALID_VALUES = {
  status: ['active', 'archived', 'all'],
  dateWindow: ['all', 'upcoming', 'current', 'past', 'ongoing', 'unscheduled'],
  publicState: ['all', 'enabled', 'disabled'],
  registrationState: ['all', 'open', 'frontEndDisabled', 'external'],
  galleryState: ['all', 'enabled', 'disabled'],
  capacityState: ['all', 'enabled', 'session', 'disabled'],
  sortOrder: [
    'startDate|desc',
    'startDate|asc',
    'endDate|desc',
    'updatedAt|desc',
    'name|asc',
    'name|desc',
  ],
};

const cleanChoice = (value, key) =>
  VALID_VALUES[key].includes(value) ? value : DEFAULT_EVENTS_VIEW_SETTINGS[key];

export const normalizeEventsViewSettings = (settings = {}) => {
  const source = settings && typeof settings === 'object' ? settings : {};

  return {
    ...DEFAULT_EVENTS_VIEW_SETTINGS,
    searchText:
      typeof source.searchText === 'string' ? source.searchText : '',
    status: cleanChoice(source.status, 'status'),
    dateWindow: cleanChoice(source.dateWindow, 'dateWindow'),
    publicState: cleanChoice(source.publicState, 'publicState'),
    registrationState: cleanChoice(
      source.registrationState,
      'registrationState',
    ),
    galleryState: cleanChoice(source.galleryState, 'galleryState'),
    capacityState: cleanChoice(source.capacityState, 'capacityState'),
    sortOrder: cleanChoice(source.sortOrder, 'sortOrder'),
  };
};

export const getEventsViewStorageKey = (user) => {
  const identity = user?.user?.uid || user?.uid || user?.email || 'anonymous';
  return `${STORAGE_PREFIX}:v1:${encodeURIComponent(identity)}`;
};

export const readEventsViewSettings = (user) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_EVENTS_VIEW_SETTINGS;
  }

  try {
    const value = window.localStorage.getItem(getEventsViewStorageKey(user));
    return normalizeEventsViewSettings(value ? JSON.parse(value) : null);
  } catch {
    return DEFAULT_EVENTS_VIEW_SETTINGS;
  }
};

export const writeEventsViewSettings = (user, settings) => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  window.localStorage.setItem(
    getEventsViewStorageKey(user),
    JSON.stringify(normalizeEventsViewSettings(settings)),
  );
};

export const shouldFetchArchivedEvents = (settings) =>
  normalizeEventsViewSettings(settings).status !== 'active';

const toComparableDate = (value) => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.valueOf() : null;
};

const getEventName = (event) => event?.sysname || event?.name || '';

const textMatches = (event, searchText) => {
  const query = searchText.trim().toLowerCase();
  if (!query) return true;

  return [
    event?.name,
    event?.sysname,
    event?.publicUrl,
    event?.campaignCode,
    event?.campaignAltCode,
    event?.id,
  ].some((value) => String(value || '').toLowerCase().includes(query));
};

export const getEventDateWindow = (event, now = new Date()) => {
  if (event?.ongoingEvent) return 'ongoing';

  const start = event?.startDate ? moment(event.startDate).startOf('day') : null;
  if (!start?.isValid()) return 'unscheduled';

  const end = event?.endDate
    ? moment(event.endDate).startOf('day')
    : start.clone();
  const today = moment(now).startOf('day');

  if (end.isBefore(today)) return 'past';
  if (start.isAfter(today)) return 'upcoming';
  return 'current';
};

const dateWindowMatches = (event, dateWindow, now) => {
  if (dateWindow === 'all') return true;

  const eventWindow = getEventDateWindow(event, now);
  if (dateWindow === 'current') {
    return eventWindow === 'current' || eventWindow === 'ongoing';
  }
  return eventWindow === dateWindow;
};

const statusMatches = (event, status) => {
  if (status === 'all') return true;
  if (status === 'archived') return event?.status === 'Archived';
  return event?.status !== 'Archived';
};

const publicMatches = (event, publicState) => {
  if (publicState === 'all') return true;
  const enabled = !!event?.allowPublic;
  return publicState === 'enabled' ? enabled : !enabled;
};

const registrationState = (event) => {
  if (event?.disableRegistration) return 'external';
  if (event?.disableFrontEndRegistration) return 'frontEndDisabled';
  if (event?.allowReg === false) return 'frontEndDisabled';
  return 'open';
};

const registrationMatches = (event, registrationFilter) =>
  registrationFilter === 'all' ||
  registrationState(event) === registrationFilter;

const booleanMatches = (value, filter) => {
  if (filter === 'all') return true;
  return filter === 'enabled' ? !!value : !value;
};

const capacityMatches = (event, capacityFilter) => {
  if (capacityFilter === 'all') return true;
  if (capacityFilter === 'session') {
    return !!event?.allowCapacity && event?.capacityType === 'session';
  }
  if (capacityFilter === 'enabled') return !!event?.allowCapacity;
  return !event?.allowCapacity;
};

export const eventMatchesFilters = (event, settings, now = new Date()) => {
  const normalized = normalizeEventsViewSettings(settings);

  return (
    textMatches(event, normalized.searchText) &&
    statusMatches(event, normalized.status) &&
    dateWindowMatches(event, normalized.dateWindow, now) &&
    publicMatches(event, normalized.publicState) &&
    registrationMatches(event, normalized.registrationState) &&
    booleanMatches(event?.allowGallery, normalized.galleryState) &&
    capacityMatches(event, normalized.capacityState)
  );
};

const compareValues = (a, b, dir) => {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (a < b) return -1 * dir;
  if (a > b) return 1 * dir;
  return 0;
};

const sortValue = (event, key) => {
  if (key === 'name') return getEventName(event).toLowerCase();
  if (key === 'startDate') return toComparableDate(event?.startDate);
  if (key === 'endDate') return toComparableDate(event?.endDate);
  if (key === 'updatedAt') return toComparableDate(event?.updatedAt);
  return null;
};

export const sortEvents = (events, sortOrder = 'startDate|desc') => {
  const normalized = normalizeEventsViewSettings({ sortOrder });
  const [key, direction] = normalized.sortOrder.split('|');
  const dir = direction === 'asc' ? 1 : -1;

  return [...(events || [])].sort((a, b) => {
    const primary = compareValues(sortValue(a, key), sortValue(b, key), dir);
    if (primary !== 0) return primary;

    return (
      compareValues(getEventName(a).toLowerCase(), getEventName(b).toLowerCase(), 1) ||
      compareValues(a?.id || '', b?.id || '', 1)
    );
  });
};

export const getFilteredEvents = (events, settings, now = new Date()) => {
  const normalized = normalizeEventsViewSettings(settings);
  return sortEvents(
    (events || []).filter((event) => eventMatchesFilters(event, normalized, now)),
    normalized.sortOrder,
  );
};

export const hasDefaultEventsViewSettings = (settings) => {
  const normalized = normalizeEventsViewSettings(settings);
  return Object.keys(DEFAULT_EVENTS_VIEW_SETTINGS).every(
    (key) => normalized[key] === DEFAULT_EVENTS_VIEW_SETTINGS[key],
  );
};
